// Centralized plan configuration for all pricing, features, and Stripe IDs

export const PLAN_LIMITS = {
  starter: {
    leads: 50,
    templates: 5,
    aiGeneration: false,
    webScraping: false,
    emailVerification: false,
    bulkEmail: false,
    automation: false,
    companyInsights: false,
    emailPatternDetection: false,
    bulkVerification: false,
  },
  growth: {
    leads: Infinity,
    templates: Infinity,
    aiGeneration: true,
    webScraping: true,
    emailVerification: true,
    bulkEmail: true,
    automation: true,
    companyInsights: false,
    emailPatternDetection: false,
    bulkVerification: false,
  },
  enterprise: {
    leads: Infinity,
    templates: Infinity,
    aiGeneration: true,
    webScraping: true,
    emailVerification: true,
    bulkEmail: true,
    automation: true,
    companyInsights: true,
    emailPatternDetection: true,
    bulkVerification: true,
  },
  teams: {
    leads: Infinity,
    templates: Infinity,
    aiGeneration: true,
    webScraping: true,
    emailVerification: true,
    bulkEmail: true,
    automation: true,
    companyInsights: true,
    emailPatternDetection: true,
    bulkVerification: true,
  },
} as const;

// Monthly credit caps for email intelligence features
export const USAGE_CAPS = {
  starter: {
    domainSearches: 0,
    emailFinds: 0,
    verifications: 0,
  },
  growth: {
    domainSearches: 75,
    emailFinds: 30,
    verifications: 150,
  },
  enterprise: {
    domainSearches: 300,
    emailFinds: 150,
    verifications: 750,
  },
  teams: {
    domainSearches: 750,
    emailFinds: 300,
    verifications: 1500,
  },
} as const;

// Beta launch: current prices ARE the real prices.
// "Original" prices are 30% higher (displayed with strikethrough).
export const BETA_LAUNCH = {
  isActive: true,
  discountLabel: "Beta Launch — 30% Off",
  discountPercent: 30,
} as const;

export const PLANS = {
  starter: {
    id: "starter",
    name: "Starter",
    monthlyPrice: "Free",
    annualPrice: "Free",
    originalMonthlyPrice: null as string | null,
    originalAnnualPrice: null as string | null,
    annualSavings: null as string | null,
    monthlyPriceId: null as string | null,
    annualPriceId: null as string | null,
    productId: null as string | null,
  },
  growth: {
    id: "growth",
    name: "Growth",
    monthlyPrice: "$49/mo",
    annualPrice: "$470/yr",
    originalMonthlyPrice: "$69/mo",
    originalAnnualPrice: "$670/yr",
    annualSavings: "Save $118",
    monthlyPriceId: "price_1T76qy2YwLpx3ck2gkEF9Wnj",
    annualPriceId: "price_1T76qz2YwLpx3ck26kHhlDRp",
    productId: "prod_U5HPTDfXD0GGPP",
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    monthlyPrice: "$149/mo",
    annualPrice: "$1,430/yr",
    originalMonthlyPrice: "$209/mo",
    originalAnnualPrice: "$2,000/yr",
    annualSavings: "Save $358",
    monthlyPriceId: "price_1T76qz2YwLpx3ck2FTBoltDN",
    annualPriceId: "price_1T76qz2YwLpx3ck2EA3ZKO7k",
    productId: "prod_U5HP8qbl4qj0IF",
  },
  teams: {
    id: "teams",
    name: "Teams",
    monthlyPrice: "$249/mo",
    annualPrice: "$2,390/yr",
    originalMonthlyPrice: "$349/mo",
    originalAnnualPrice: "$3,390/yr",
    annualSavings: "Save $598",
    monthlyPriceId: "price_1T76r02YwLpx3ck2Imx0DDBq",
    annualPriceId: "price_1T76r02YwLpx3ck24FibzDTw",
    productId: "prod_U5HPuCbQkwxgOo",
  },
} as const;

// Partner program coupon ID
export const PARTNER_COUPON_ID = "8dBMMEWg";

export const PLAN_FEATURES: Record<PlanId, string[]> = {
  starter: [
    "Up to 50 leads/month",
    "5 email templates",
    "Manual lead entry",
    "Basic lead management",
    "Email support",
  ],
  growth: [
    "Unlimited leads",
    "Unlimited templates",
    "AI-powered lead generation",
    "Web scraping tools",
    "Email verification",
    "Bulk email campaigns",
    "Email automation",
    "75 domain searches/month",
    "150 email verifications/month",
    "Priority support",
  ],
  enterprise: [
    "Everything in Growth",
    "Company insights panel",
    "Email pattern detection",
    "Bulk email verification",
    "Advanced automation",
    "300 domain searches/month",
    "750 email verifications/month",
    "Priority email delivery",
    "Advanced analytics",
    "Dedicated support",
  ],
  teams: [
    "Everything in Enterprise",
    "Team collaboration workspace",
    "Kanban task management",
    "Multi-view task board (List, Calendar, Timeline)",
    "Shared lead lists & contacts",
    "Team member roles (Owner, Admin, Member, Guest)",
    "Task assignments & notifications",
    "750 domain searches/month",
    "1,500 email verifications/month",
    "Dedicated account manager",
  ],
};

export type PlanId = keyof typeof PLANS;
export type FeatureKey = keyof typeof PLAN_LIMITS.starter;

// Helper to get price ID based on plan and billing interval
export const getPriceId = (planId: PlanId, isAnnual: boolean): string | null => {
  const plan = PLANS[planId];
  return isAnnual ? plan.annualPriceId : plan.monthlyPriceId;
};

// Helper to check if a plan is paid
export const isPaidPlan = (planId: PlanId): boolean => {
  return planId !== "starter";
};

// Helper to get usage caps for a plan
export const getUsageCaps = (planId: PlanId) => {
  return USAGE_CAPS[planId];
};
